"use client";

import { memo } from "react";
import Image from "next/image";
import Link from "@/components/ui/Link";
import { useRouter } from "next/navigation";
import { MessageIcon } from "../ui/message-icon";
import { BOOKMARK_CATEGORIES } from "@/lib/api/public-client";
import { FALLBACK_IMAGE } from '@/lib/constants';
import { getImageUrl, getTimeAgo, getSectionUrl, getFocusPointStyle, ImageMetadata, BLUR_PLACEHOLDER } from '@/lib/componentutills';

type FieldConfig = {
  hidden?: boolean;
  value?: string;
  customClass?: string;
};

type HorizontalNewsItem = {
  category?: FieldConfig;
  sectionUrl?: string;
  title?: FieldConfig;
  excerpt?: FieldConfig;
  timeAgo?: FieldConfig;
  imageSrc?: FieldConfig;
  slug?: string;
  imageMetadata?: ImageMetadata;
  templatetype?: FieldConfig;
};

type Props = HorizontalNewsItem & {
  data?: HorizontalNewsItem[];
  /** Mark as LCP image - preloads for faster rendering */
  priority?: boolean;
  imageMetadata?: ImageMetadata;
};

function HorizontalBigImageCategoryNews({
  data,
  category = { hidden: false, value: "INDIA", customClass: "" },
  sectionUrl,
  title = { hidden: false, value: "இந்தியாவின் நிலக்கரி உற்பத்தி 8.5 சதவீதம் அதிகரிப்பு; நிலக்கரி அமைச்சகம்", customClass: "" },
  excerpt = { hidden: false, value: "நாட்டின் நிலக்கரி உற்பத்தி 2023 ஏப்ரலில் 8.5 சதவீதம் அதிகரித்து 73.14 கோடி டன்னாக", customClass: "" },
  timeAgo = { hidden: false, value: "15 min ago", customClass: "" },
  imageSrc = { hidden: false, value: FALLBACK_IMAGE, customClass: "" },
  slug,
  priority = false,
  imageMetadata,
  templatetype = { hidden: false, value: "", customClass: "" },

}: Props) {
  const router = useRouter();
  const items = data ?? [
    { category, title, excerpt, timeAgo, imageSrc, slug, sectionUrl },
  ];

  const renderCard = ({
    category: itemCategory,
    title: itemTitle,
    excerpt: itemExcerpt,
    timeAgo: itemTimeAgo,
    imageSrc: itemImageSrc,
    slug: itemSlug,
    sectionUrl: itemSectionUrl,
    imageMetadata: itemImageMetadata,
    templatetype: itemTemplatetype,
  }: HorizontalNewsItem) => {
    const catVal = itemCategory?.value ?? category?.value ?? "";
    const isLiveBlog = templatetype?.value === "live-blog" || templatetype?.value === "true";
   
    const titleVal = itemTitle?.value ?? title?.value ?? "";
    const excerptVal = itemExcerpt?.value ?? excerpt?.value ?? "";
    const timeVal = getTimeAgo(itemTimeAgo?.value ?? timeAgo?.value);
    const imgVal = getImageUrl(itemImageSrc?.value ?? imageSrc?.value) || FALLBACK_IMAGE;
    const sectionUrlVal = getSectionUrl(itemSectionUrl ?? sectionUrl);

    return (
      <article className="bg-F8F8F8 overflow-hidden p-4 mb-0">
        <div className="flex gap-4">
          {/* LEFT: IMAGE */}
          {!(itemImageSrc?.hidden ?? imageSrc?.hidden) && (
            <div className={`shrink-0 md:w-5/12 lg:w-[37%] ${itemImageSrc?.customClass ?? imageSrc?.customClass ?? ""}`}>
              {/* Mobile: explicit 106x60 (16/9-ish). Desktop: pure aspect-ratio.
                  Removed conflicting min-h-[180px] which competed with aspect-ratio
                  and let height float between two values, causing CLS. */}
              <div className="relative w-[106px] h-[60px] md:w-full md:h-auto md:aspect-[16/9] overflow-hidden" style={{ contain: 'layout' }}>
                <Image
                  src={imgVal}
                  alt={titleVal}
                  fill
                  priority={priority}
                  sizes="(max-width: 768px) 100px, (max-width: 1200px) 50vw, 400px"
                  className="object-cover"
                  style={getFocusPointStyle(itemImageMetadata ?? imageMetadata)}
                  loading={priority ? "eager" : "lazy"}
                  fetchPriority={priority ? "high" : "auto"}
                  placeholder="blur"
                  blurDataURL={BLUR_PLACEHOLDER}
                />
              </div>
            </div>
          )}

          {/* RIGHT: CONTENT */}
          <div className="flex flex-col justify-between lg:min-h-[180px] w-full">
            <div className="flex flex-col gap-2 md:gap-3">
              {!(itemCategory?.hidden ?? category?.hidden) && (
                <>
                  {sectionUrlVal ? (
                    <span
                      className={`text-11-tamil-600 md:text-14-tamil-600 text-012061 tracking-wide uppercase hover:underline cursor-pointer ${itemCategory?.customClass ?? category?.customClass ?? ""}`}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        router.push(sectionUrlVal);
                      }}
                    >
                      {catVal}
                    </span>
                  ) : (
                    <div className={`text-11-tamil-600 md:text-14-tamil-600 text-012061 tracking-wide uppercase ${itemCategory?.customClass ?? category?.customClass ?? ""}`}>
                      {catVal}
                    </div>
                  )}
                </>
              )}

              {!(itemTitle?.hidden ?? title?.hidden) && (
 <h3 className={`text-14-tamil-700 md:text-12-tamil-600 text-012061 font-medium leading-[1.4] ${itemTitle?.customClass ?? title?.customClass ?? ""}`}>
                  {isLiveBlog && (
                    <span className="inline-flex items-center mr-2 align-middle">
                      <span className="relative inline-flex h-4 w-4 items-center justify-center">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-40 animate-[wave_1.5s_ease-out_infinite]" />
                        <span className="absolute inline-flex h-3 w-3 rounded-full bg-red-500 opacity-60 animate-[wave_1.5s_ease-out_0.3s_infinite]" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-red-600" />
                      </span>
                    </span>
                  )}
                  {titleVal}
                </h3>
              )}

              {!(itemExcerpt?.hidden ?? excerpt?.hidden) && (
                <p className={`text-12-tamil-400 md:text-12-tamil-400 text-4D4D4D leading-[1.4] line-clamp-2 ${itemExcerpt?.customClass ?? excerpt?.customClass ?? ""}`}>
                  {excerptVal}
                </p>
              )}
            </div>

            {/* Bottom Footer */}
            <div className="mt-2">
              <div className="flex items-center justify-between">
                {!(itemTimeAgo?.hidden ?? timeAgo?.hidden) && (
                  <span className={`text-12-english-500 text-1B1B1B ${itemTimeAgo?.customClass ?? timeAgo?.customClass ?? ""}`}>
                    {timeVal}
                  </span>
                )}
                <MessageIcon
                  id="image-news-message-icon"
                  slug={itemSlug ?? slug}
                  category={BOOKMARK_CATEGORIES.NEWS}
                  title={titleVal}
                  thumbnailUrl={imgVal}
                  description={excerptVal}
                  articleUrl={itemSlug ? `/${itemSlug}` : undefined}
                />
              </div>
            </div>
          </div>
        </div>
      </article>
    );
  };

  return (
    <div className="space-y-4">
      {items.map((item, index) => {
        const card = renderCard(item);

        if (item.slug) {
          return (
            <Link
              key={item.slug ?? `${item.title?.value}-${index}`}
              href={`/${item.slug}`}
              className="block  transition-colors cursor-pointer"
            >
              {card}
            </Link>
          );
        }

        return <div key={item.slug ?? `${item.title?.value}-${index}`}>{card}</div>;
      })}
    </div>
  );
}

// Memoize to prevent unnecessary re-renders
export default memo(HorizontalBigImageCategoryNews);
